import * as React from 'react';
import './DataCrafterTemplateImportWizard.less';
import { DataCrafterTemplatesVisualStore } from '../../stores';
import { Observer, observer } from 'mobx-react-lite';
import { Modal, Table, Select, Button, Spin } from 'antd';
import { DataCrafterTemplateImportDropdownValue, DataCrafterTemplateImportMapping } from '../../types';

type Props = {
    store: DataCrafterTemplatesVisualStore;
};

const DataCrafterTemplateImportWizard: React.FC<Props> = ({ store }) => {
    const bundledValuePrefix = '__bundled:';

    const onCancel = () => {
        if (store.templateCommitLoading) {
            return;
        }

        store.setTemplateImportDialogVisible(false);
        store.resetTemplateImportWizard();
    };

    const onSubmit = () => {
        store.commitImportedTemplate();
    };

    // Handle model selection change
    const handleModelChange = (value: string, modelRole: string) => {
        store.templateImportCommitData.setModelMapping(modelRole, value);
    };

    // Handle package selection change
    const handlePackageChange = (
        targetPkg: string,
        sourcePkg: string,
        mappingTableType: 'Input Packages' | 'Output Packages'
    ) => {
        if (mappingTableType === 'Input Packages') {
            store.templateImportCommitData.setInputPackageMapping(sourcePkg, targetPkg);
        } else {
            store.templateImportCommitData.setOutputPackageMapping(sourcePkg, targetPkg);
        }
    };

    if (!store.templateImportData) {
        return null;
    }

    // Define model selection table columns
    const modelColumns = [
        {
            title: 'Model',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 200
        },
        {
            title: '',
            dataIndex: 'select',
            key: 'select',
            width: 500,
            render: (_: any, record: DataCrafterTemplateImportMapping) => {
                const allowedBundledValues = new Set(record.altOptions ?? []);
                return (
                    <Observer>
                        {() => (
                            <Select<string>
                                style={{ width: '100%' }}
                                placeholder="Select a model"
                                value={store.templateImportCommitData.modelMappings[record.id]}
                                onChange={value => handleModelChange(value, record.id)}
                                options={store.templateImportData?.modelSelectionOptions
                                    .filter(
                                        (option: DataCrafterTemplateImportDropdownValue) =>
                                            !option.value.startsWith(bundledValuePrefix) ||
                                            allowedBundledValues.has(option.value)
                                    )
                                    .map((option: DataCrafterTemplateImportDropdownValue) => ({
                                        label: option.label,
                                        value: option.value,
                                        title: option.label
                                    }))}
                                showSearch
                                filterOption={(text, option) =>
                                    option?.title && option.title.toLowerCase().includes(text.toLowerCase())
                                }
                            />
                        )}
                    </Observer>
                );
            }
        }
    ];

    // Define package mapping table columns
    const packageColumns = [
        {
            title: 'Package',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Type',
            dataIndex: 'tableType',
            key: 'tableType',
            width: 200
        },
        {
            title: '',
            dataIndex: 'select',
            key: 'select',
            width: 500,
            render: (_: any, record: DataCrafterTemplateImportMapping) => {
                const allowedBundledValues = new Set(record.altOptions ?? []);
                return (
                    <Observer>
                        {() => (
                            <Select<string>
                                style={{ width: '100%' }}
                                placeholder="Select a package"
                                onChange={value =>
                                    handlePackageChange(
                                        value,
                                        record.id,
                                        record.tableType as 'Input Packages' | 'Output Packages'
                                    )
                                }
                                value={
                                    record.tableType === 'Input Packages'
                                        ? store.templateImportCommitData.inputPackageMappings[record.id]
                                        : store.templateImportCommitData.outputPackageMappings[record.id]
                                }
                                options={store.templateImportData?.packageSelectionOptions
                                    .filter(
                                        (option: DataCrafterTemplateImportDropdownValue) =>
                                            !option.value.startsWith(bundledValuePrefix) ||
                                            allowedBundledValues.has(option.value)
                                    )
                                    .map((option: DataCrafterTemplateImportDropdownValue) => ({
                                        label: option.label,
                                        value: option.value,
                                        title: option.label
                                    }))}
                                showSearch
                                filterOption={(text, option) =>
                                    option?.title && option.title.toLowerCase().includes(text.toLowerCase())
                                }
                            />
                        )}
                    </Observer>
                );
            }
        }
    ];

    // Define model selection table data
    const modelData = store.templateImportData.modelMapperTableData ?? [
        {
            id: 'planner',
            name: 'Agent Model',
            tableType: 'Models' as const,
            type: 'LLM',
            bestValue: null
        },
        {
            id: 'coder',
            name: 'Code Generation Model',
            tableType: 'Models' as const,
            type: 'LLM',
            bestValue: null
        }
    ];

    return (
        <Modal
            title="Import Data Crafter Template"
            visible={store.templateImportDialogVisible}
            onCancel={onCancel}
            maskClosable={false}
            destroyOnClose={false}
            closable={false}
            width={1300}
            className="alpha-portal-dialog rule-import table scrollable full-screen"
            centered
            footer={[
                <Button
                    data-id="iota-add-group-dialog-cancel"
                    size="large"
                    className="light"
                    key="back"
                    onClick={onCancel}
                    disabled={store.templateCommitLoading}
                >
                    Cancel
                </Button>,
                <Button
                    className="dark"
                    data-id="rules-import-page-import-button"
                    size="large"
                    key="submit"
                    type="primary"
                    onClick={onSubmit}
                    loading={store.templateCommitLoading}
                    disabled={store.templateCommitLoading || !store.templateImportCommitData.isValid}
                >
                    Submit
                </Button>
            ]}
        >
            <Spin spinning={store.templateCommitLoading} tip="Importing template...">
                <div className="model-selection-section">
                    <h3>Model Selection</h3>
                    <Table
                        rowKey={record => record.id}
                        columns={modelColumns}
                        dataSource={modelData}
                        pagination={false}
                        size="middle"
                    />
                </div>
                <div className="package-mapping-section">
                    <h3>Package Mapping</h3>
                    <Table
                        rowKey={record => record.id}
                        columns={packageColumns}
                        dataSource={store.templateImportData.mapperTableData}
                        pagination={false}
                        size="middle"
                    />
                </div>
            </Spin>
        </Modal>
    );
};

export default observer(DataCrafterTemplateImportWizard);
